<template>
  <div class="py-8 px-9">
    <v-container fluid class="pa-0">
      <v-form ref="StudentProfiling">
        <div>
          <div style="width: 100%">
            <!-- Page header -->
            <div
              class="mb-6 d-flex flex-wrap justify-space-between align-center ga-2"
            >
              <div>
                <div class="page-title">Student Profiling Form</div>
                <div class="text-subtitle-2 mt-1">
                  <span class="text-grey"
                    >Complete your profile to help us match you with the best
                    internship opportunities</span
                  >
                </div>
              </div>
              <v-chip
                v-if="!isUpdating"
                size="small"
                class="status-chip status-approved"
                label
              >
                <v-icon start size="16">mdi-check-circle</v-icon>
                Profile Saved
              </v-chip>
              <v-chip
                v-else
                size="small"
                class="status-chip status-review"
                label
              >
                <v-icon start size="16">mdi-pencil</v-icon>
                Editing
              </v-chip>
            </div>

            <div class="form-card rounded-lg pa-6">
              <div class="d-flex justify-end mb-4">
                <v-btn
                  :color="
                    isUpdating
                      ? $vuetify.theme.themes.light.submitBtnsGreen
                      : $vuetify.theme.themes.light.primary
                  "
                  rounded="lg"
                  elevation="0"
                  class="text-none"
                  @click="!isUpdating ? update() : save()"
                >
                  <v-icon start size="18">{{
                    !isUpdating
                      ? "mdi-pencil-outline"
                      : "mdi-content-save-outline"
                  }}</v-icon>
                  {{ !isUpdating ? "Update" : "Save" }} Profile
                </v-btn>
              </div>

              <!-- Add this block after the form header description and before the skillsList section -->

              <div class="mt-2 section-card rounded-lg pa-4">
                <div class="d-flex align-center">
                  <v-avatar size="32" class="section-icon-wrap">
                    <v-icon color="#10B981" size="18"
                      >mdi-account-outline</v-icon
                    >
                  </v-avatar>
                  <div class="text-subtitle-1 mx-2 font-weight-bold">
                    Personal Information
                  </div>
                </div>
                <div class="text-caption text-grey mb-3">
                  Please fill in your personal details.
                  <span style="color: red">*</span>
                </div>

                <v-row>
                  <v-col cols="4">
                    <v-text-field
                      :readonly="!isUpdating"
                      v-model="fname"
                      label="First Name"
                      :rules="[formRules.required]"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      class="rounded-lg"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      :readonly="!isUpdating"
                      v-model="mname"
                      label="Middle Name"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      class="rounded-lg"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      :readonly="!isUpdating"
                      v-model="lname"
                      label="Last Name"
                      :rules="[formRules.required]"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      class="rounded-lg"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      :readonly="!isUpdating"
                      v-model="suffix"
                      label="Suffix"
                      placeholder="e.g. Jr., Sr., III"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      class="rounded-lg"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      :readonly="!isUpdating"
                      v-model="bdate"
                      label="Birthdate"
                      type="date"
                      :rules="[formRules.required]"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      class="rounded-lg"
                    />
                  </v-col>

                  <v-col cols="4">
                    <v-autocomplete
                      :readonly="!isUpdating"
                      v-model="sex"
                      label="Sex"
                      :rules="[formRules.required]"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      class="rounded-lg"
                      :items="['Male', 'Female']"
                    />
                  </v-col>
                </v-row>
              </div>

              <div
                class="mt-4 section-card rounded-lg pa-4"
                v-if="skillsList.length > 0"
              >
                <div class="d-flex align-center">
                  <v-avatar size="32" class="section-icon-wrap">
                    <v-icon color="#10B981" size="18"
                      >mdi-briefcase-outline</v-icon
                    >
                  </v-avatar>
                  <div class="text-subtitle-1 mx-2 font-weight-bold">
                    Your Skills
                  </div>
                </div>
                <div class="text-caption text-grey">
                  Select all skills that apply to you.
                  <span style="color: red">*</span>
                </div>

                <v-row class="mt-2">
                  <v-col cols="4" v-for="i in skillsList" :key="i.id">
                    <div
                      class="pa-1 rounded-lg selectable-tile"
                      :class="
                        selectedSkills.includes(i.title)
                          ? 'selectable-tile-active'
                          : ''
                      "
                      :style="isUpdating ? 'cursor: pointer' : ''"
                      @click="isUpdating ? toggleSkill(i.title) : null"
                    >
                      <v-checkbox
                        :readonly="!isUpdating"
                        :label="i.title"
                        :model-value="selectedSkills.includes(i.title)"
                        hide-details
                        color="#10B981"
                        @click.prevent
                      ></v-checkbox>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <div class="mt-4 section-card rounded-lg pa-4">
                <div class="d-flex align-center">
                  <v-avatar size="32" class="section-icon-wrap">
                    <v-icon color="#10B981" size="18">mdi-map-marker</v-icon>
                  </v-avatar>
                  <div class="text-subtitle-1 mx-2 font-weight-bold">
                    Deployment Preference
                  </div>
                </div>
                <div class="text-caption text-grey">
                  Prefered location / Area for Deployment
                  <span style="color: red">*</span>
                </div>

                <div class="mt-2">
                  <!-- <v-text-field
                      label="Address"
                      :color="$vuetify.theme.themes.light.inputColor"
                      variant="outlined"
                      density="compact"
                      placeholder="e.g. Davao City"
                    /> -->
                  <v-autocomplete
                    v-model="location"
                    :readonly="!isUpdating"
                    dense
                    class="rounded-lg"
                    :rules="[formRules.required]"
                    item-value="id"
                    item-title="location"
                    :color="$vuetify.theme.themes.light.inputColor"
                    variant="outlined"
                    density="compact"
                    :items="locationList"
                  >
                  </v-autocomplete>
                </div>
                <div class="mt-2">
                  <div class="text-subtitle-1 font-weight-bold">
                    Mode of Training <span style="color: red">*</span>
                  </div>
                  <div class="mt-2 d-flex justify-space-between">
                    <div
                      style="width: 30%"
                      :style="isUpdating ? 'cursor: pointer' : ''"
                      @click="isUpdating ? select_mode_of_training(1) : null"
                      class="selectable-tile py-4 px-4 rounded-lg text-center"
                      :class="
                        mode_of_training == 1 ? 'selectable-tile-active' : ''
                      "
                    >
                      <div class="text-subtitle-1 font-weight-bold">
                        Face to Face
                      </div>
                      <div
                        class="text-caption"
                        :class="
                          mode_of_training == 1
                            ? 'text-green-darken-2'
                            : 'text-grey'
                        "
                      >
                        Onsite Training
                      </div>
                    </div>
                    <div
                      style="width: 30%"
                      :style="isUpdating ? 'cursor: pointer' : ''"
                      @click="isUpdating ? select_mode_of_training(2) : null"
                      class="selectable-tile py-4 px-4 rounded-lg text-center"
                      :class="
                        mode_of_training == 2 ? 'selectable-tile-active' : ''
                      "
                    >
                      <div class="text-subtitle-1 font-weight-bold">Hybrid</div>
                      <div
                        class="text-caption"
                        :class="
                          mode_of_training == 2
                            ? 'text-green-darken-2'
                            : 'text-grey'
                        "
                      >
                        Mix on-site & remote
                      </div>
                    </div>
                    <div
                      style="width: 30%"
                      :style="isUpdating ? 'cursor: pointer' : ''"
                      @click="isUpdating ? select_mode_of_training(3) : null"
                      class="selectable-tile py-4 px-4 rounded-lg text-center"
                      :class="
                        mode_of_training == 3 ? 'selectable-tile-active' : ''
                      "
                    >
                      <div class="text-subtitle-1 font-weight-bold">
                        Work From Home
                      </div>
                      <div
                        class="text-caption"
                        :class="
                          mode_of_training == 3
                            ? 'text-green-darken-2'
                            : 'text-grey'
                        "
                      >
                        Full Remote Training
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-4">
                  <div
                    class="text-subtitle-1 font-weight-bold d-flex align-center"
                  >
                    Own a device?
                    <v-checkbox
                      :readonly="!isUpdating"
                      hide-details
                      color="#10B981"
                      v-model="hasOwnDevice"
                    ></v-checkbox>
                  </div>
                  <div
                    class="mt-2 d-flex justify-space-around"
                    v-if="hasOwnDevice"
                  >
                    <div
                      style="width: 250px"
                      :style="isUpdating ? 'cursor: pointer' : ''"
                      v-for="(i, idx) in deviceList"
                      :key="idx"
                      @click="isUpdating ? setOwnDevice(i) : null"
                      class="selectable-tile py-4 px-4 rounded-lg text-center"
                      :class="
                        selectedDevice.length > 0 && selectedDevice.includes(i)
                          ? 'selectable-tile-active'
                          : ''
                      "
                    >
                      <div class="text-subtitle-1 font-weight-bold">
                        {{ i }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-6" v-if="added_form_field.length > 0">
                  <div
                    class="text-subtitle-1 font-weight-bold d-flex align-center"
                  >
                    Added Fields
                  </div>
                  <div class="mt-2">
                    <div v-for="i in added_form_field" :key="i">
                      <v-text-field
                        :disabled="!isUpdating"
                        v-if="i.field_type == 'Textfield'"
                        :color="$vuetify.theme.themes.light.inputColor"
                        variant="outlined"
                        :rules="[formRules.required]"
                        density="compact"
                        class="rounded-lg"
                        :label="i.field_name"
                        v-model="i.data"
                      ></v-text-field>
                      <v-textarea
                        v-if="i.field_type == 'Textarea'"
                        :disabled="!isUpdating"
                        rows="3"
                        :color="$vuetify.theme.themes.light.inputColor"
                        variant="outlined"
                        :rules="[formRules.required]"
                        density="compact"
                        class="rounded-lg"
                        :label="i.field_name"
                        v-model="i.data"
                      ></v-textarea>

                      <div
                        v-if="i.field_type == 'Radio Button'"
                        class="text-subtitle-1"
                      >
                        {{ i.field_name }}
                      </div>

                      <v-radio-group
                        inline
                        v-if="i.field_type == 'Radio Button'"
                        v-model="i.data"
                        :disabled="!isUpdating"
                        color="#10B981"
                      >
                        <v-radio
                          v-for="(j, index) in i.field_options"
                          :key="index"
                          :value="j.option_name"
                          :label="j.option_name"
                        ></v-radio>
                      </v-radio-group>

                      <v-autocomplete
                        :disabled="!isUpdating"
                        v-if="i.field_type == 'Dropdown'"
                        dense
                        v-model="i.data"
                        :label="i.field_name"
                        class="rounded-lg"
                        item-value="option_name"
                        item-title="option_name"
                        :color="$vuetify.theme.themes.light.inputColor"
                        variant="outlined"
                        density="compact"
                        :items="i.field_options"
                      >
                      </v-autocomplete>
                    </div>
                  </div>
                </div>

                <div class="mt-8 d-flex justify-end">
                  <v-btn
                    :color="
                      isUpdating
                        ? $vuetify.theme.themes.light.submitBtnsGreen
                        : $vuetify.theme.themes.light.primary
                    "
                    rounded="lg"
                    elevation="0"
                    class="text-none"
                    @click="!isUpdating ? update() : save()"
                  >
                    <v-icon start size="18">{{
                      !isUpdating
                        ? "mdi-pencil-outline"
                        : "mdi-content-save-outline"
                    }}</v-icon>
                    {{ !isUpdating ? "Update" : "Save" }} Profile
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-form>
    </v-container>

    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :top="fadeAwayMessage.top"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>
<script>
// import eventBus from "@/eventBus";
export default {
  name: "ProfilePage",

  data() {
    return {
      updateDetails: false,
      fname: null,
      mname: null,
      lname: null,
      suffix: null,
      sex: null,
      bdate: null,
      action: null,
      taggingData: null,
      mode_of_training: null,
      skillsList: [],
      isUpdating: false,
      selectedSkills: [],
      locationList: [],
      deviceList: ["Laptop", "Desktop Computer"],
      selectedDevice: [],
      hasOwnDevice: false,
      added_form_field: [],
      address: null,
      location: null,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "System Message!",
        message: "",
        top: 10,
      },
      readonly: true,
      credentialReadonly: true,
    };
  },
  mounted() {
    this.initialize();
    this.getLocations();
    this.loadSkillList();
    this.loadAddedFormFields();
  },
  beforeUnmount() {},

  methods: {
    initialize() {
      this.axiosCall(
        "/preferred-area-for-deployment/getMyProfiling",
        "GET",
      ).then((res) => {
        if (res.data) {
          this.isUpdating = false;
          const skills = res.data.profiling?.skills;
          if (Array.isArray(skills)) {
            this.selectedSkills = [...skills];
          } else if (typeof skills === "string") {
            try {
              const parsed = JSON.parse(skills);
              this.selectedSkills = Array.isArray(parsed) ? [...parsed] : [];
            } catch {
              this.selectedSkills = [];
            }
          } else {
            this.selectedSkills = [];
          }
          this.mode_of_training = res.data.profiling?.mode_of_training;
          this.location = res.data.profiling?.prefered_location_id;
          this.hasOwnDevice = res.data.profiling?.hasOwnDevice == 1;
          this.selectedDevice = res.data.profiling
            ? res.data.profiling?.deviceOwned
            : [];
          this.fname = res.data.data.fname;
          this.mname = res.data.data.mname;
          this.lname = res.data.data.lname;
          this.suffix = res.data.data.suffix;
          this.bdate = res.data.data.bdate;
          this.sex = res.data.data.sex;
        }
      });
    },

    loadSkillList() {
      this.axiosCall("/skill-assessement/getSkillCategory", "GET").then(
        (res) => {
          if (res.data) {
            console.log(res.data);
            this.skillsList = res.data;
          }
        },
      );
    },

    loadAddedFormFields() {
      this.axiosCall("/student/getAddedFormFields", "GET").then((res) => {
        if (res.data) {
          console.log("Added FORM ", res.data);
          this.added_form_field = res.data;
        }
      });
    },

    select_mode_of_training(val) {
      this.mode_of_training = val;
    },

    setOwnDevice(i) {
      if (!this.selectedDevice.includes(i)) {
        this.selectedDevice.push(i);
      } else {
        let idx = this.selectedDevice.indexOf(i);

        this.selectedDevice.splice(idx, 1);
        console.log("called", this.selectedDevice);
      }
    },

    toggleSkill(skill) {
      if (!this.selectedSkills.includes(skill)) {
        this.selectedSkills.push(skill);
      } else {
        const idx = this.selectedSkills.indexOf(skill);
        this.selectedSkills.splice(idx, 1);
      }
    },

    async save() {
      let validation = await this.$refs.StudentProfiling.validate();
      let valid = true;
      if (
        this.selectedSkills.length == 0 &&
        !this.location &&
        !this.mode_of_training
      ) {
        if (this.hasOwnDevice) {
          if (this.selectedDevice.length == 0) {
            valid = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message =
              "Please fill in all required fields.";
            this.fadeAwayMessage.header = "System Message";
          }
        } else {
          valid = false;
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.message = "Please fill in all required fields.";
          this.fadeAwayMessage.header = "System Message";
        }
      }

      if (validation.valid) {
        if (valid) {
          // console.log(
          //   this.mode_of_training,
          //   this.selectedSkills,
          //   this.location,
          // );
          let data = {
            mode_of_training: this.mode_of_training,
            selectedSkills: JSON.stringify(this.selectedSkills),
            location: this.location,
            selectedDevice: this.hasOwnDevice
              ? JSON.stringify(this.selectedDevice)
              : [],
            hasOwnDevice: this.hasOwnDevice,
            added_form_field: JSON.stringify(this.added_form_field),
            fname: this.fname,
            mname: this.mname,
            lname: this.lname,
            suffix: this.suffix,
            sex: this.sex,
            bdate: this.bdate,
          };
          this.axiosCall("/student/save-student-profiling", "PATCH", data).then(
            (res) => {
              if (res.data.status == 200) {
                this.fadeAwayMessage.show = true;
                this.fadeAwayMessage.type = "success";
                this.fadeAwayMessage.message = res.data.msg;
                this.fadeAwayMessage.header = "System Message";
                this.initialize();
              } else if (res.data.status == 400) {
                this.fadeAwayMessage.show = true;
                this.fadeAwayMessage.type = "error";
                this.fadeAwayMessage.message = res.data.msg;
                this.fadeAwayMessage.header = "System Message";
              }
            },
          );
        }
      }
    },

    update() {
      this.isUpdating = true;
    },

    getLocations() {
      this.axiosCall(
        "/preferred-area-for-deployment/getForStudentProfiling",
        "GET",
        this.data,
      ).then((res) => {
        if (res.data) {
          this.locationList = res.data;
        }
      });
    },
    onFileChanged() {
      const uploadedimg = document.getElementById("fileUpload").files[0];
      this.selectFile = uploadedimg;

      this.previewImg = URL.createObjectURL(uploadedimg);
      this.isSelecting = true;
    },
  },
  created() {
    this.initialize();
    // console.log("created");
    if (this.$store.state.expiryDate < Date.now()) {
      this.$store.dispatch("setUser", null);
      this.$store.dispatch("setIsAuthenticated", 0);
      this.render = true;
      this.$router.push("/");
      // location.reload();
    }
  },
};
</script>
<style scoped>
.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #1a1a1a;
}

.form-card {
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  background-color: #ffffff;
}

.section-card {
  border: 1px solid #e5e7eb;
  background-color: #fafafa;
  transition: box-shadow 0.2s;
}

.section-icon-wrap {
  background-color: #10b98120;
}

/* Status chips (matches Students.vue theme) */
.status-chip {
  border-radius: 6px;
  font-weight: 600;
}
.status-approved {
  background-color: #dcfce7;
  color: #15803d;
}
.status-review {
  background-color: #e0f2fe;
  color: #0369a1;
}

/* Selectable tiles (mode of training, device, skills) */
.selectable-tile {
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  transition: border-color 0.15s, background-color 0.15s;
}
.selectable-tile-active {
  border: 1.5px solid #10b981;
  background-color: #10b9811a;
}
</style>
